import {
  BoundCheckboxField,
  BoundNumberField,
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  ScrollableContent,
  ScrollableFooter,
  simpleHeader,
} from "@homebound/beam";
import { FieldState, ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationCheck } from "src/components";
import { DisplayNamedFragment } from "src/fragments/commonFragments.generated";
import { CostType, Maybe, SaveBidContractLineItemInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { fail, groupBy, isNumber, queryResult } from "src/utils";
import {
  EnterUnitBasedBidPage_BidContractRevisionFragment,
  EnterUnitBasedBidPage_BidItemFragment,
  EnterUnitBasedBidPage_BidPackageFragment,
  EnterUnitBasedBidPageDocument,
  useEnterUnitBasedBidPage_SaveBidContractRevisionMutation,
  useEnterUnitBasedBidPageQuery,
} from "./EnterUnitBasedBidPage.generated";

export function EnterUnitBasedBidPage() {
  const { bidPackageId, bidderId } = useRouteParams<{ bidPackageId: string; bidderId: string }>();
  const query = useEnterUnitBasedBidPageQuery({ variables: { bidPackageId } });
  return queryResult(query, ({ bidPackage }) => (
    <EnterUnitBasedBidPageView bidPackage={bidPackage} bidderId={bidderId} />
  ));
}

type EnterUnitBasedBidPageViewProps = { bidPackage: EnterUnitBasedBidPage_BidPackageFragment; bidderId: string };

function EnterUnitBasedBidPageView(props: EnterUnitBasedBidPageViewProps) {
  const { bidPackage, bidderId } = props;
  const bidder =
    bidPackage.activeBidders.find((b) => b.id === bidderId) ??
    fail(`Bidder ${bidderId} not found on bid package ${bidPackage.id}`);
  // AddTradePartnerModal creates a BC + draft BCR for new bidders, and goToEnterCostsPage
  // handles contracts whose revision stranded on an older version, so the BCR must exist by
  // the time the user lands on this page.
  const bidContractId = bidder.bidContract?.id ?? fail(`Bidder ${bidder.id} has no bid contract`);
  const bidContractRevision =
    bidPackage.latestVersion.latestBidContractRevisions.find((b) => b.bidContract.id === bidContractId) ??
    fail(`Bid contract ${bidContractId} has no revision on the latest version of bid package ${bidPackage.id}`);

  const [saveBidContractRevision] = useEnterUnitBasedBidPage_SaveBidContractRevisionMutation({
    refetchQueries: [EnterUnitBasedBidPageDocument],
  });

  const onSave = useCallback(
    async (lineItems: SaveBidContractLineItemInput[]) => {
      const result = await saveBidContractRevision({ variables: { input: { id: bidContractRevision.id, lineItems } } });
      return result.data?.saveBidContractRevision.bidContractRevision;
    },
    [bidContractRevision.id, saveBidContractRevision],
  );

  return (
    <>
      <div css={Css.xl2.tac.py4.fs0.$}>Enter Costs</div>
      <UnitBasedBidsEnterTable
        bidPackage={bidPackage}
        bidContractRevision={bidContractRevision}
        tradePartnerName={bidder.name}
        onSave={onSave}
      />
    </>
  );
}

type UnitBasedBidsEnterTableProps = {
  bidPackage: EnterUnitBasedBidPage_BidPackageFragment;
  bidContractRevision: EnterUnitBasedBidPage_BidContractRevisionFragment;
  tradePartnerName: string;
  onSave: (
    lineItems: SaveBidContractLineItemInput[],
  ) => Promise<EnterUnitBasedBidPage_BidContractRevisionFragment | null | undefined>;
};

function UnitBasedBidsEnterTable(props: UnitBasedBidsEnterTableProps) {
  const { bidPackage, bidContractRevision, tradePartnerName, onSave } = props;
  const navigate = useNavigate();

  const formState = useFormState({
    config: formConfig,
    init: {
      input: { lineItems: bidPackage.latestVersion.lineItems, bidContractRevision },
      map: ({ lineItems, bidContractRevision: bcr }) => buildEditCosts(lineItems, bcr),
    },
  });

  const { useRegisterNavigationCheck } = useNavigationCheck();
  useRegisterNavigationCheck(() => !formState.dirty, [formState]);

  const [usedLineItems, unusedLineItems] = bidPackage.latestVersion.lineItems.partition((li) => li.isInUse);
  // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const usedRows = useMemo(() => createRows(usedLineItems, formState), [usedLineItems, bidPackage, formState]);
  const unusedRows = useMemo(
    () => createRows(unusedLineItems, formState, true),
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unusedLineItems, bidPackage, formState],
  );
  const columns = useMemo(() => createColumns(tradePartnerName), [tradePartnerName]);

  return (
    <>
      <ScrollableContent virtualized>
        <div css={Css.df.w100.aic.gap2.py2.pr2.$}>
          <div css={Css.lg.w100.$}>Items</div>
        </div>
        <GridTable stickyHeader columns={columns} rows={usedRows} id="usedBidsTable" />
        {unusedLineItems.nonEmpty && (
          <>
            <div css={Css.py5.$} />
            <div css={Css.w100.aic.gap2.py2.pr2.$}>
              <div css={Css.lg.w100.$}>Unused Items</div>
              <div css={Css.sm.$}>
                These items are no longer included in this bid's product offerings. Unused items will not be included
                when you download this bid sheet.
              </div>
            </div>
            <GridTable columns={columns} rows={unusedRows} id="unusedBidsTable" />
          </>
        )}
      </ScrollableContent>
      <ScrollableFooter>
        <div css={Css.df.px6.py3.bshHover.bgWhite.$}>
          <div css={Css.mla.df.gap2.$}>
            <Button
              size="lg"
              onClick={createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id)}
              label="Cancel"
              variant="quaternary"
            />
            <Button
              size="lg"
              onClick={async () => {
                const saved = await onSave(
                  formState.costs.rows.filter((r) => isNumber(r.value.totalCostInCents)).map((r) => r.value),
                );
                if (saved) {
                  formState.update({ lineItems: bidPackage.latestVersion.lineItems, bidContractRevision: saved });
                }
                navigate(createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id));
              }}
              label="Save"
            />
          </div>
        </div>
      </ScrollableFooter>
    </>
  );
}

type HeaderRow = { kind: "header" };
type CostCodeRow = { kind: "costCode"; data: { costCode: DisplayNamedFragment } };
type ItemRow = { kind: "item"; data: { item: DisplayNamedFragment } };
type BidItemRow = {
  kind: "bidItem";
  data: {
    bidItem: EnterUnitBasedBidPage_BidItemFragment;
    costField: FieldState<Maybe<number>>;
    isTaxExemptField: FieldState<Maybe<boolean>>;
  };
};
type Row = HeaderRow | CostCodeRow | ItemRow | BidItemRow;

type FormValues = {
  costs: Pick<SaveBidContractLineItemInput, "id" | "totalCostInCents" | "bidItemId" | "isTaxExempt">[];
};

const formConfig: ObjectConfig<FormValues> = {
  costs: {
    type: "list",
    update: "incremental",
    config: {
      id: { type: "value" },
      bidItemId: { type: "value" },
      totalCostInCents: { type: "value" },
      isTaxExempt: { type: "value" },
    },
  },
};

function createRows(
  lineItems: EnterUnitBasedBidPage_BidPackageFragment["latestVersion"]["lineItems"],
  formState: ObjectState<FormValues>,
  initCollapsed = false,
): GridDataRow<Row>[] {
  const bidItemRows = lineItems.map(({ bidItem }) => {
    const row = formState.costs.rows.find((r) => r.value.bidItemId === bidItem.id)!;
    return {
      kind: "bidItem" as const,
      id: bidItem.id,
      data: { bidItem, costField: row.totalCostInCents, isTaxExemptField: row.isTaxExempt },
    };
  });

  const itemGroupBy = groupBy(bidItemRows, (i) => i.data.bidItem.items[0].id);
  const itemRows = Object.keys(itemGroupBy).map((key) => {
    const children = itemGroupBy[key];
    return {
      kind: "item" as const,
      id: key,
      data: { item: children[0].data.bidItem.items[0] },
      children,
      ...{ initCollapsed },
    };
  });

  const costCodeGroupBy = groupBy(itemRows, (o) => o.data.item.costCode.id);
  const costCodeRows = Object.keys(costCodeGroupBy).map((key) => {
    const children = costCodeGroupBy[key];
    return {
      kind: "costCode" as const,
      id: key,
      data: { costCode: children[0].data.item.costCode },
      children,
      ...{ initCollapsed },
    };
  });

  return [simpleHeader, ...costCodeRows];
}

function createColumns(tradePartnerName: string): GridColumn<Row>[] {
  return [
    collapseColumn<Row>(),
    column<Row>({
      header: "Name",
      costCode: ({ costCode }) => ({ content: costCode.displayName, css: Css.smSb.$ }),
      item: ({ item }) => item.displayName,
      bidItem: ({ bidItem }) =>
        bidItem.parentMaterialVariant?.displayName ?? bidItem.parentTask?.name ?? bidItem.displayName,
    }),
    column<Row>({
      header: "Material Code",
      costCode: emptyCell,
      item: emptyCell,
      bidItem: ({ bidItem }) => bidItem.parentMaterialVariant?.code,
    }),
    column<Row>({
      header: "UoM",
      costCode: emptyCell,
      item: emptyCell,
      bidItem: ({ bidItem }) => bidItem.unitOfMeasure.shortName,
    }),
    column<Row>({
      header: "Tax Exempt",
      costCode: emptyCell,
      item: emptyCell,
      bidItem: ({ isTaxExemptField }) => <BoundCheckboxField field={isTaxExemptField} label="" />,
    }),
    numericColumn<Row>({
      header: tradePartnerName,
      costCode: emptyCell,
      item: emptyCell,
      bidItem: ({ costField }) => {
        return { content: <BoundNumberField field={costField} type="cents" />, alignment: "left" };
      },
    }),
  ];
}

function buildEditCosts(
  lineItems: EnterUnitBasedBidPage_BidPackageFragment["latestVersion"]["lineItems"],
  bidContractRevision: EnterUnitBasedBidPage_BidContractRevisionFragment,
): FormValues {
  return {
    costs: lineItems.map(({ bidItem }) => {
      const bcli = bidContractRevision.lineItems.find((li) => li.bidItem?.id === bidItem.id);
      return {
        id: bcli?.id,
        bidItemId: bidItem.id,
        // $0 missing cost placeholders (userSet=false) seed as unentered
        totalCostInCents: bcli?.userSet ? bcli.totalCostInCents : undefined,
        isTaxExempt: bcli?.isTaxExempt ?? bidItem.costType.code !== CostType.Materials,
      };
    }),
  };
}
